import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/for-artists", label: "For Artists" },
  { to: "/for-fans", label: "For Fans" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/why-audiomine", label: "Why AudioMine" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,border-color] duration-300 ${
        scrolled ? "am-frost border-b border-black/5" : "bg-transparent"
      }`}
    >
      <div className="container flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full am-tile-pink shadow-[0_8px_20px_-4px_rgba(250,37,104,0.4)]">
            <span className="h-2.5 w-2.5 rounded-full bg-white" />
          </span>
          <span className="font-display font-extrabold tracking-tight text-lg md:text-xl text-foreground">
            AudioMine
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace("/", "")}`}
              className="am-navlink px-3 py-2 text-sm font-medium text-foreground/75 hover:text-foreground transition-colors"
              data-active={pathname === n.to ? "true" : "false"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/contact" className="hidden md:inline-flex">
            <Button
              data-testid="nav-cta-get-started"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-5 h-10 shadow-[0_8px_24px_-6px_rgba(250,37,104,0.55)] hover:shadow-[0_12px_30px_-6px_rgba(250,37,104,0.75)] transition-shadow"
            >
              Get Started
            </Button>
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-[0_2px_10px_rgba(15,15,20,0.08)] border border-black/5"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-panel" className="lg:hidden am-frost border-t border-black/5">
          <div className="container py-6 flex flex-col gap-3">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-mobile-${n.to.replace("/", "")}`}
                className="text-lg font-display font-semibold text-foreground/85 hover:text-primary py-1"
              >
                {n.label}
              </Link>
            ))}
            <Link to="/contact" data-testid="nav-mobile-cta" className="mt-2">
              <Button className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 font-semibold">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
